import React, { useState } from 'react';

import Button from '@atlaskit/button/custom-theme-button';
import Check from '@atlaskit/icon/glyph/check';
import Warning from '@atlaskit/icon/glyph/warning';
import { Popup } from '@atlaskit/popup';
import { Stack } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { Commit as CommitType } from 'src/components/types';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';

import { messages } from '../../gpg-keys.i18n';
import { isValidSignature } from '../../utils';

import { GpgKeysUnverifiedCard } from './unverified-card';
import { GpgKeysVerifiedCard } from './verified-card';

export type GpgKeysCommitVerificationProps = {
  commit: CommitType;
};

export const GpgKeysCommitVerification = ({
  commit,
}: GpgKeysCommitVerificationProps): JSX.Element | null => {
  const [isOpen, setIsOpen] = useState(false);
  const hasGpgKeyFeature = useStatsigGate(StatsigFeatureKeys.gpgKeysUi);
  const { formatMessage } = useIntl();

  const verification = commit.extra?.verification;

  if (!hasGpgKeyFeature || !verification) {
    return null;
  }

  return (
    <Stack>
      {isValidSignature(verification.status) ? (
        <Popup
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          placement="right"
          content={() => <GpgKeysVerifiedCard commit={commit} />}
          trigger={triggerProps => (
            <Tooltip
              content={formatMessage(messages.verifiedLabel)}
              position="top"
            >
              <Button
                {...triggerProps}
                onClick={() => setIsOpen(!isOpen)}
                appearance="link"
                spacing="default"
                iconBefore={
                  <Check
                    label={formatMessage(messages.verifiedLabel)}
                    primaryColor={token('color.icon', colors.N500)}
                  />
                }
                label={formatMessage(messages.verifiedLabel)}
              />
            </Tooltip>
          )}
        />
      ) : (
        <Popup
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          placement="right"
          content={() => <GpgKeysUnverifiedCard />}
          trigger={triggerProps => (
            <Tooltip
              content={formatMessage(messages.unverifiedLabel)}
              position="top"
            >
              <Button
                {...triggerProps}
                onClick={() => setIsOpen(!isOpen)}
                appearance="link"
                spacing="default"
                iconBefore={
                  <Warning
                    label={formatMessage(messages.unverifiedLabel)}
                    primaryColor={token(
                      'color.background.warning.bold',
                      colors.Y300
                    )}
                  />
                }
                label={formatMessage(messages.unverifiedLabel)}
              />
            </Tooltip>
          )}
        />
      )}
    </Stack>
  );
};
